import React, { useEffect, useState } from "react";
import {useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { ArrowLeftIcon } from '@heroicons/react/solid';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Invoice.css';
import { QRCodeSVG } from "qrcode.react";
import {
  Hash,
  Package,
  FileText,
  Building2,
  CreditCard,
  PackageCheck,
  DollarSign,
  Calculator,
  MoreHorizontal,
  FileEdit,
  User,
  IdCard,
  UserRound,
  Cake,
  XCircle,
} from "lucide-react";
  import { motion } from "framer-motion";
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Utility function to convert numbers to words
const convertToWords = (num) => {
  const units = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"
  ];
  const teens = [
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", 
    "Sixteen", "Seventeen", "Eighteen", "Nineteen"
  ];
  const tens = [
    "", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];
  const thousands = ["", "Thousand", "Million", "Billion"];

  if (num === 0) return "Zero";

  const getChunkWords = (chunk) => {
    let words = "";
    if (chunk >= 100) {
      words += units[Math.floor(chunk / 100)] + " Hundred ";
      chunk %= 100;
    }
    if (chunk >= 11 && chunk <= 19) {
      words += teens[chunk - 11] + " ";
    } else {
      words += tens[Math.floor(chunk / 10)] + " ";
      chunk %= 10;
      words += units[chunk] + " ";
    }
    return words.trim();
  };

  let word = "";
  let chunkIndex = 0;
  while (num > 0) {
    const chunk = num % 1000;
    if (chunk > 0) {
      word = getChunkWords(chunk) + " " + thousands[chunkIndex] + " " + word;
    }
    num = Math.floor(num / 1000);
    chunkIndex++;
  }

  return word.trim();
};

const convertAmountToWords = (amount) => {
  
  const [birrPart, centPart] = amount.toString().split(".");

  let words = convertToWords(parseInt(birrPart)) + " Birr";
  if (centPart && parseInt(centPart) > 0) {
    words += " and " + "xx/100" +" " + "Cents";
  }
  return words;
};

const MedicationInvoiceQrcode = () => {
  const [invalidData, setInvalidData] = useState(false);
  const [orderDetails, setOrderDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("patientId");
  const invoiceNumber = searchParams.get("invoice_number");
  const token = searchParams.get("token");
  const [patientDetails, setPatientDetails] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [systemData, setSystemData] = useState({
      phoneNumber: '',
      tinNumber: '',
      address: '',
      email: '',
      titleAmharic: '',
      titleEnglish: '',
    });
    
    useEffect(() => {
    // Fetch the current settings from the backend
    const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/setting/get-settings`);
      setSystemData(response.data);
    } catch (error) {
      toast.error("There is no setting data.");
      console.error(error);
    }
    };
    
    fetchSettings();
    }, []);


   useEffect(() => {
    fetch(`${BASE_URL}/patient/view-all-patients-based-on-ward/${patientId}`)
      .then((res) => res.json())
      .then((data) => {
        setPatientDetails(data);
      })
      .catch((err) => console.error("Error fetching patient details:", err));
  }, [patientId]);
  const formatDate = () => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date().toLocaleDateString(undefined, options);
  };

   useEffect(() => {
    if (!token || !invoiceNumber || !patientId) {
      setInvalidData(true);
      setLoading(false);
      return;
    }

    const fetchMedicationOrders = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/patient/view-medication-qrcode-invoice`, {
          params: { invoiceNumber, patientId, token }
        });

        if (!response.data || response.data.length === 0) {
          setInvalidData(true);
        } else {
          setOrderDetails(response.data);
        }
      } catch (error) {
        console.error("Error fetching medication order data:", error);
        setInvalidData(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMedicationOrders();
  }, [token, invoiceNumber, patientId]);

  useEffect(() => {
    const calculatedTotal = orderDetails.reduce(
      (sum, order) => sum + (order.total_price || 0),
      0
    );
    setTotalPrice(calculatedTotal);
    setGrandTotal(calculatedTotal - (calculatedTotal * discount) / 100);
  }, [orderDetails, discount]);
  const totalPriceWords = convertAmountToWords(totalPrice);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-500">
        Loading...
      </div>
    );
  }
  // 🚨 Invalid Data UI
    if (invalidData) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-green-50">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-center p-8 rounded-2xl shadow-lg bg-white border border-green-300"
          >
            <motion.div 
              animate={{ rotate: [0, -10, 10, -10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
              className="flex justify-center"
            >
              <XCircle className="w-20 h-20 text-green-600" />
            </motion.div>
            <h2 className="mt-4 text-2xl font-bold text-green-700">Invalid Data</h2>
            <p className="text-green-600 mt-2">The medication order for this invoice could not be found.</p>
          </motion.div>
        </div>
      );
    }
  return ( 
    <div className="min-h-[80vh] w-full md:min-h-screen bg-gray-100 mt-24 px-2 sm:px-4">
    <ToastContainer />
      <div className="flex-grow text-right">
      <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 hidden-print"
      >
          Print
      </button>
      </div>
          <div className="px-2 mx-auto bg-white border border-black rounded-lg shadow-md print-invoice">
            {/* Logo and Header Section */}
            <div className="text-center">
            <img src="/assets/hiywot_logo.jpg" alt="hiywot_logo" className="mx-auto h-24" />
            <div className="text-lg">
              <p className="text-base font-bold text-gray-800">
                <span className="font-semibold text-blue-600">Email:</span> {systemData.email} |
                
                <span className="font-bold text-blue-600">
                  TIN <span className="underline">No</span>:
                </span>  {systemData.tinNumber} |

                <span className="font-bold text-blue-600">
                Phone <span className="underline">No</span> :
                  </span> {systemData.phoneNumber} |
                <span className="font-bold text-blue-600"> Address:</span> {systemData.address}
              </p>
              <h2 className="text-center text-lg font-bold underline text-gray-900 mb-2">
                Cash Attachment
              </h2>
            </div>
          </div>
          {patientDetails && (
            <div className="flex flex-wrap gap-6 mb-4 mt-2">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-blue-600" />
                <p className="font-bold">Client name:</p>
                <p>{patientDetails.name} {patientDetails.fname} {patientDetails.gfname}</p>
              </div>
              <div className="flex items-center gap-2">
                <IdCard className="w-4 h-4 text-green-600" />
                <p className="font-bold">MRN:</p>
                <p>{patientDetails.mrn}</p>
              </div>
              <div className="flex items-center gap-2">
                <UserRound className="w-4 h-4 text-pink-500" />
                <p className="font-bold">Sex:</p>
                <p>{patientDetails.sex}</p>
              </div>
              <div className="flex items-center gap-2">
                <Cake className="w-4 h-4 text-yellow-600" />
                <p className="font-bold">Age:</p>
                <p>{patientDetails.age}</p>
              </div>
            </div>
          )}
       {/* 🌈 Gradient Separator Line */}
       <div className="h-1 w-full rounded-full mb-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 mt-2" />
        {/* Order Details */}
            <div className="overflow-x-auto">
            {orderDetails.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse border border-gray-300 text-xs sm:text-sm md:text-md">
                <thead>
                <tr className="bg-gray-300">
                  <th className="border border-gray-300 text-sm">
                    <div className="flex items-center gap-1 text-yellow-600 font-semibold">
                      <Hash className="w-4 h-4" />
                    </div>
                  </th>
                  <th className="border border-gray-300 text-sm">
                    <div className="flex items-center gap-1 text-blue-700 font-semibold">
                      <Package className="w-4 h-4" /> <span>Item</span>
                    </div>
                  </th>
                  <th className="border border-gray-300 text-sm">
                    <div className="flex items-center gap-1 text-purple-700 font-semibold">
                      <FileText className="w-4 h-4" /> <span>Description</span>
                    </div>
                  </th>
                  <th className="border border-gray-300 text-sm">
                    <div className="flex items-center gap-1 text-orange-600 font-semibold">
                      <PackageCheck className="w-4 h-4" /> <span>Quantity</span>
                    </div>
                  </th>
                  <th className="border border-gray-300 text-sm">
                    <div className="flex items-center gap-1 text-green-700 font-semibold">
                      <DollarSign className="w-4 h-4" /> <span>Unit Price</span>
                    </div>
                  </th>
                  <th className="border border-gray-300 text-sm">
                    <div className="flex items-center gap-1 text-rose-600 font-semibold">
                      <Calculator className="w-4 h-4" /> <span>Total Price</span>
                    </div>
                  </th>
                  <th className="hidden-print border text-sm border-gray-300">
                    <div className="flex items-center gap-1 text-gray-700 font-semibold">
                      <MoreHorizontal className="w-4 h-4" /> <span>Action</span>
                    </div>
                  </th>
                </tr>
              </thead>
                  <tbody>
                    {orderDetails.map((order, index) => (
                      <tr key={index} className="">
                      <td className="border border-gray-300">{index + 1}</td>
                        <td className="border border-gray-300">{order.item}</td>
                        <td className="border border-gray-300">{order.description}</td>
                        <td className="border border-gray-300">{order.quantity}</td>
                        <td className="border border-gray-300">{(Number(order.unit_price) || 0).toFixed(2)}</td>
                        <td className="border border-gray-300">{(Number(order.total_price) || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between py-2">
                <div className="h-[10%] w-[10%]">
                <QRCodeSVG 
                  value={`https://asratmediumclinic.com/medication-invoice?invoice_number=${invoiceNumber}&patientId=${patientId}&token=${token}`} 
                  size={128}
                  />
                  </div>
                {/* Amount in Words Section */}
                <div className="flex flex-col items-start">
                  <span className="font-semibold text-sm text-gray-900">Amount in Words</span>
                  {totalPriceWords} Only
                </div>
                {/* Rounded Table-like Container */}
                <div className="relative bg-white rounded-lg w-fit overflow-hidden before:absolute before:top-0 before:left-0 before:h-full before:w-2 before:rounded-tr before:rounded-br before:bg-gradient-to-b before:from-pink-500 before:via-purple-500 before:to-blue-500">
                  <table className="w-full border-collapse ml-2">
                    <tbody>
                      {/* Subtotal Row */}
                      <tr className="border-b border-gray-300">
                        <td className="text-left font-semibold text-sm text-gray-900">Subtotal</td>
                        <td className="border-l-2 border-gray-300 pl-4 text-right font-semibold text-sm text-gray-900">
                          {(Number(totalPrice) || 0).toFixed(2)} ETB
                        </td>
                      </tr>
                      {/* VAT Row */}
                      <tr className="border-b border-gray-300">
                        <td className="text-left font-semibold text-sm text-gray-900">VAT</td>
                        <td className="border-l-2 border-gray-300 pl-4 text-right font-semibold text-sm text-gray-900">
                          0.00 ETB
                        </td>
                      </tr>
                      <tr>
                        <td className="text-left font-semibold text-sm text-gray-900">Grand Total</td>
                        <td className="border-l-2 border-gray-300 pl-4 text-right font-semibold text-sm text-gray-900">
                        {(Number(grandTotal) || 0).toFixed(2)} Birr
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
            ) : (
              <p className="text-gray-500 text-lg">No orders found.</p>
            )}
            </div>
            <div className="pt-4 text-sm">
            {/* Additional text section */}
            <footer className="print-footer text-center text-sm text-gray-700">
            <p className="text-sm font-medium">
              Original copy to payer / Second copy to Accountant / Cashier - Invalid without Fiscal Receipt Attachment
            </p>
            <p className="text-xs">
            Developed by Humble | +251 964681997 | Hossana, Ethiopia
            </p>
          </footer>
          </div>
          </div>
          </div>
       );
     };

export default MedicationInvoiceQrcode;
